import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  SelectChangeEvent,
  Card,
  CardContent,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Assessment as ScoreIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  ArrowBack as ArrowBackIcon,
  Feedback as FeedbackIcon,
} from '@mui/icons-material';
import { resumeService } from '../services/resumeService';
import { feedbackService } from '../services/feedbackService';

interface Resume {
  id: string;
  candidateName: string;
  candidateEmail?: string;
  email?: string;
  phone?: string;
  jobId: string;
  jobTitle?: string;
  status: string;
  score?: number;
  uploadedDate?: string;
  assignedSME?: string;
  filePath?: string;
  fileName?: string;
  assignedPanel?: string;
  experience?: number;
  location?: string;
  skills?: string[];
  notes?: string;
}

interface InterviewPanel {
  id: string;
  name: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  members: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface ResumeViewUpdateDialogProps {
  open: boolean;
  resume: Resume | null;
  onClose: () => void;
  onUpdate: (resumeId: string, updates: any) => void;
  onDownload: (resumeId: string, fileName?: string) => void;
  userRole: string;
  loading?: boolean;
}

// Resume status options from API
const RESUME_STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted', color: '#3b82f6', bgColor: '#dbeafe' },
  { value: 'reviewing', label: 'Reviewing', color: '#f59e0b', bgColor: '#fef3c7' },
  {
    value: 'interview_scheduled',
    label: 'Interview Scheduled',
    color: '#8b5cf6',
    bgColor: '#ede9fe',
  },
  { value: 'rejected', label: 'Rejected', color: '#ef4444', bgColor: '#fee2e2' },
  { value: 'hired', label: 'Hired', color: '#10b981', bgColor: '#d1fae5' },
  { value: 'withdrawn', label: 'Withdrawn', color: '#6b7280', bgColor: '#f3f4f6' },
  { value: 'shortlisted', label: 'Shortlisted', color: '#06b6d4', bgColor: '#cffafe' },
  { value: 'interviewed', label: 'Interviewed', color: '#84cc16', bgColor: '#ecfccb' },
  {
    value: 'move_to_next_panel',
    label: 'Move to Next Panel',
    color: '#f97316',
    bgColor: '#fed7aa',
  },
];

const ResumeViewUpdateDialog: React.FC<ResumeViewUpdateDialogProps> = ({
  open,
  resume,
  onClose,
  onUpdate,
  onDownload,
  userRole,
  loading = false,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [panels, setPanels] = useState<InterviewPanel[]>([]);
  const [loadingPanels, setLoadingPanels] = useState(false);
  const [resumePreview, setResumePreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [panelsError, setPanelsError] = useState<string | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);

  // Feedback state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Reset to view mode when modal opens/closes or resume changes
  useEffect(() => {
    if (open && resume) {
      setEditMode(false);

      setFormData({
        candidateName: resume.candidateName || '',
        candidateEmail: resume.candidateEmail || resume.email || '',
        phone: resume.phone || '',
        status: resume.status || 'submitted',
        assignedPanel: resume.assignedPanel || resume.assignedSME || '',
        notes: resume.notes || '',
        skills: resume.skills || [],
      });

      setPreviewError(null);
      setPanelsError(null);

      loadInterviewPanels();
      loadResumePreview();
    } else if (!open) {
      setEditMode(false);
      setFormData({});
      setPanels([]);
      setResumePreview(null);
      setPreviewError(null);
      setPanelsError(null);
      // Clean up object URL when closing
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
        setPreviewObjectUrl(null);
      }
    }
  }, [open, resume]);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [previewObjectUrl]);

  const handleClose = () => {
    setEditMode(false);
    onClose();
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (resume) {
      setFormData({
        candidateName: resume.candidateName || '',
        candidateEmail: resume.candidateEmail || resume.email || '',
        phone: resume.phone || '',
        status: resume.status || 'submitted',
        assignedPanel: resume.assignedPanel || resume.assignedSME || '',
        notes: resume.notes || '',
        skills: resume.skills || [],
      });
    }
  };

  const handleBackToView = () => {
    setEditMode(false);
    if (resume) {
      setFormData({
        candidateName: resume.candidateName || '',
        candidateEmail: resume.candidateEmail || resume.email || '',
        phone: resume.phone || '',
        status: resume.status || 'submitted',
        assignedPanel: resume.assignedPanel || resume.assignedSME || '',
        notes: resume.notes || '',
        skills: resume.skills || [],
      });
    }
  };

  const handleDownload = () => {
    if (resume) {
      onDownload(resume.id, resume.fileName);
    }
  };

  const loadInterviewPanels = async () => {
    setLoadingPanels(true);
    setPanelsError(null);

    try {
      console.log('Loading interview panelists...');

      const response = await resumeService.getAvailablePanelists();

      console.log('Panelists response:', response);

      if (response && response.panels && Array.isArray(response.panels)) {
        setPanels(response.panels);
        console.log('Loaded panelists:', response.panels);
      } else {
        setPanels([]);
        setPanelsError(
          'No active panelists found. Contact administrator to set up interview panels.'
        );
      }
    } catch (error) {
      console.error('Error loading interview panelists:', error);
      setPanelsError(error instanceof Error ? error.message : 'Failed to load interview panelists');
      setPanels([]);
    } finally {
      setLoadingPanels(false);
    }
  };

  const loadResumePreview = async () => {
    if (!resume?.id) return;

    setLoadingPreview(true);
    setPreviewError(null);

    // Clean up previous object URL if exists
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
    }

    try {
      console.log('Loading preview for resume:', resume.id);
      const preview = await resumeService.getResumePreview(resume.id);
      console.log('Preview loaded successfully:', {
        hasFileUrl: !!preview.fileUrl,
        hasFileBlob: !!preview.fileBlob,
        fileType: preview.fileType,
        fileExtension: preview.fileExtension,
        fileName: preview.fileName
      });
      
      setResumePreview(preview);
      
      // Store the object URL for cleanup
      if (preview.fileUrl) {
        setPreviewObjectUrl(preview.fileUrl);
        console.log('Preview object URL set:', preview.fileUrl);
      } else {
        console.warn('No fileUrl in preview response');
      }
    } catch (error) {
      console.error('Error loading resume preview:', error);
      setPreviewError(error instanceof Error ? error.message : 'Failed to load resume preview');
      setResumePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };


  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSkillsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    handleInputChange('skills', typeof value === 'string' ? value.split(',') : value);
  };

  const handleSave = async () => {
    if (!resume) return;

    try {
      setStatusUpdateLoading(true);

      // Check if panel assignment changed
      const panelChanged =
        formData.assignedPanel &&
        formData.assignedPanel !== (resume.assignedPanel || resume.assignedSME);

      if (panelChanged) {
        console.log('Panel assignment changed, updating panel and status...');

        // Use the combined method to assign panel and update status
        const result = await resumeService.assignPanelToResumeWithStatus(
          resume.id,
          formData.assignedPanel,
          {
            // Include assignment data even for basic panel assignment
            interviewDateTime: null,
            duration: 60,
            notes: '',
            avatureFeedbackLink: '',
          }
        );

        console.log('Panel assignment and status update completed:', result);

        // Update the resume object with new status
        const updatedResumeData = {
          ...formData,
          status: 'interview_scheduled', // Set the new status
        };

        // Call parent update with the new data including status
        await onUpdate(resume.id, updatedResumeData);
      } else {
        // No panel change, just update basic fields
        await onUpdate(resume.id, formData);
      }

      setEditMode(false);
    } catch (error) {
      console.error('Error saving resume:', error);
      // Handle error appropriately
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string, reason?: string, notes?: string) => {
    if (!resume) return;

    try {
      setStatusUpdateLoading(true);

      const result = await resumeService.updateResumeStatus(
        resume.id,
        newStatus,
        reason || `Status updated to ${newStatus}`,
        notes || ''
      );

      console.log('Status update result:', result);

      // Update the form data and trigger parent update
      const updatedData = {
        ...formData,
        status: newStatus,
      };

      setFormData(updatedData);
      await onUpdate(resume.id, updatedData);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const statusOption = RESUME_STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption || { color: '#6b7280', bgColor: '#f3f4f6' };
  };

  const canEdit = userRole === 'hr' || userRole === 'admin';

  if (!resume) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Enhanced Header with Back Navigation */}
        <DialogTitle
          sx={{
            backgroundColor: 'white',
            py: 3,
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  backgroundColor: '#f3f4f6',
                  color: 'var(--primary-blue)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  border: '2px solid #e5e7eb',
                }}
              >
                {getInitials(resume.candidateName)}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: '#1f2937' }}>
                  {resume.candidateName}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 0.5, color: 'var(--primary-blue)', fontWeight: 500 }}
                >
                  Resume ID: {resume.id}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                  {resume.jobId} - {resume.jobTitle || 'Unknown Job'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Mode Navigation - Back arrow when in edit mode */}
              {editMode ? (
                <Tooltip title="Back to View">
                  <IconButton
                    onClick={handleBackToView}
                    sx={{
                      backgroundColor: '#f8fafc',
                      color: '#6b7280',
                      border: '1px solid #e2e8f0',
                      borderRadius: 1.5,
                      width: 40,
                      height: 40,
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                        borderColor: '#cbd5e1',
                        color: '#475569',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                canEdit && (
                  <Tooltip title="Edit Resume">
                    <IconButton
                      onClick={() => setEditMode(true)}
                      sx={{
                        backgroundColor: '#f8fafc',
                        color: '#6b7280',
                        border: '1px solid #e2e8f0',
                        borderRadius: 1.5,
                        width: 40,
                        height: 40,
                        '&:hover': {
                          backgroundColor: '#f1f5f9',
                          borderColor: '#cbd5e1',
                          color: '#475569',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              )}

              <Tooltip title="Download Resume">
                <IconButton
                  onClick={handleDownload}
                  sx={{
                    backgroundColor: '#f8fafc',
                    color: '#6b7280',
                    border: '1px solid #e2e8f0',
                    borderRadius: 1.5,
                    width: 40,
                    height: 40,
                    '&:hover': {
                      backgroundColor: '#ecfdf5',
                      borderColor: '#bbf7d0',
                      color: '#059669',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={handleClose}
                sx={{
                  backgroundColor: '#f8fafc',
                  color: '#6b7280',
                  border: '1px solid #e2e8f0',
                  borderRadius: 1.5,
                  width: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                    borderColor: '#fecaca',
                    color: '#dc2626',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Edit Mode Indicator */}
          {editMode && (
            <Box
              sx={{
                mt: 2,
                px: 3,
                py: 1.5,
                backgroundColor: '#fef3c7',
                borderRadius: 1.5,
                border: '1px solid #fbbf24',
              }}
            >
              <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 500 }}>
                Edit Mode - Make changes and click "Save Changes" to update the resume data
              </Typography>
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: '#f8fafc' }}>
          {/* Status Bar */}
          <Box
            sx={{
              backgroundColor: 'white',
              px: 4,
              py: 2,
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Chip
                label={resume.status}
                sx={{
                  backgroundColor: getStatusConfig(resume.status).bgColor,
                  color: getStatusConfig(resume.status).color,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  height: 28,
                  borderRadius: '14px',
                  border: `1px solid ${getStatusConfig(resume.status).color}30`,
                }}
              />
              {resume.score && (
                <Chip
                  icon={<ScoreIcon sx={{ fontSize: '0.9rem !important' }} />}
                  label={`${resume.score}% Match`}
                  sx={{
                    backgroundColor:
                      resume.score >= 80 ? '#d1fae5' : resume.score >= 60 ? '#fef3c7' : '#fee2e2',
                    color:
                      resume.score >= 80 ? '#065f46' : resume.score >= 60 ? '#92400e' : '#991b1b',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    height: 28,
                    borderRadius: '14px',
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Uploaded:{' '}
              {resume.uploadedDate
                ? new Date(resume.uploadedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </Typography>
          </Box>

          {/* Alert Messages */}
          {(panelsError || loadingPanels) && (
            <Box sx={{ px: 4, pt: 2 }}>
              {panelsError && (
                <Alert severity="warning" sx={{ mb: 1, borderRadius: 2, fontSize: '0.875rem' }}>
                  <Typography variant="body2">
                    <strong>Interview Panels:</strong> {panelsError}
                  </Typography>
                </Alert>
              )}
              {loadingPanels && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    backgroundColor: '#f0f9ff',
                    borderColor: '#0ea5e9',
                    '& .MuiAlert-icon': { color: '#0ea5e9' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#0ea5e9' }} />
                    <Typography variant="body2">Loading interview panels...</Typography>
                  </Box>
                </Alert>
              )}
            </Box>
          )}

          {/* Main Content */}
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Left Column - Candidate Information */}
              <Grid item xs={12} lg={6}>
                <Stack spacing={3}>
                  {/* Basic Information Card */}
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <PersonIcon sx={{ color: 'var(--primary-blue)', fontSize: 20 }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}
                        >
                          Candidate Information
                        </Typography>
                      </Box>

                      <Stack spacing={2.5}>
                        {/* Name */}
                        <Box>
                          {editMode ? (
                            <TextField
                              fullWidth
                              label="Full Name"
                              value={formData.candidateName}
                              onChange={e => handleInputChange('candidateName', e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#f9fafb',
                                  '&:hover': { backgroundColor: '#f3f4f6' },
                                  '&.Mui-focused': { backgroundColor: 'white' },
                                },
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  p: 1.2,
                                  borderRadius: 1.5,
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <PersonIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', mb: 0.3 }}
                                >
                                  Full Name
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500, color: '#1f2937', fontSize: '0.95rem' }}
                                >
                                  {resume.candidateName}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Email */}
                        <Box>
                          {editMode ? (
                            <TextField
                              fullWidth
                              label="Email Address"
                              type="email"
                              value={formData.candidateEmail}
                              onChange={e => handleInputChange('candidateEmail', e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#f9fafb',
                                  '&:hover': { backgroundColor: '#f3f4f6' },
                                  '&.Mui-focused': { backgroundColor: 'white' },
                                },
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  p: 1.2,
                                  borderRadius: 1.5,
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <EmailIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', mb: 0.3 }}
                                >
                                  Email Address
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500, color: '#1f2937', fontSize: '0.95rem' }}
                                >
                                  {resume.candidateEmail || resume.email || 'Not provided'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Phone */}
                        <Box>
                          {editMode ? (
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={formData.phone}
                              onChange={e => handleInputChange('phone', e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#f9fafb',
                                  '&:hover': { backgroundColor: '#f3f4f6' },
                                  '&.Mui-focused': { backgroundColor: 'white' },
                                },
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  p: 1.2,
                                  borderRadius: 1.5,
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <PhoneIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', mb: 0.3 }}
                                >
                                  Phone Number
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500, color: '#1f2937', fontSize: '0.95rem' }}
                                >
                                  {resume.phone || 'Not provided'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Job Information */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              p: 1.2,
                              borderRadius: 1.5,
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BusinessIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem', mb: 0.3 }}
                            >
                              Position Applied For
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500, color: '#1f2937', fontSize: '0.95rem' }}
                            >
                              {resume.jobId} - {resume.jobTitle}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Status & Assignment Card */}
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <AssignIcon sx={{ color: 'var(--primary-blue)', fontSize: 20 }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}
                        >
                          Status & Assignment
                        </Typography>
                      </Box>

                      <Stack spacing={3}>
                        {/* Status with manual update for SME */}
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem', mb: 1.5 }}
                          >
                            Application Status
                          </Typography>
                          {editMode ? (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              <TextField
                                select
                                fullWidth
                                value={formData.status}
                                onChange={e => handleInputChange('status', e.target.value)}
                                variant="outlined"
                                size="small"
                                disabled={statusUpdateLoading}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backgroundColor: '#f9fafb',
                                    '&:hover': { backgroundColor: '#f3f4f6' },
                                    '&.Mui-focused': { backgroundColor: 'white' },
                                  },
                                }}
                              >
                                {RESUME_STATUS_OPTIONS.map(option => (
                                  <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          backgroundColor: option.color,
                                        }}
                                      />
                                      {option.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </TextField>

                              {/* Quick status update button for SME */}
                              {userRole === 'sme' && formData.status !== resume.status && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleStatusUpdate(formData.status)}
                                  disabled={statusUpdateLoading}
                                  sx={{
                                    minWidth: 120,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {statusUpdateLoading ? 'Updating...' : 'Update Status'}
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Chip
                                label={
                                  RESUME_STATUS_OPTIONS.find(opt => opt.value === resume.status)
                                    ?.label || resume.status
                                }
                                sx={{
                                  backgroundColor: getStatusConfig(resume.status).bgColor,
                                  color: getStatusConfig(resume.status).color,
                                  fontWeight: 500,
                                  borderRadius: '10px',
                                  height: 28,
                                  fontSize: '0.8rem',
                                  border: `1px solid ${getStatusConfig(resume.status).color}30`,
                                }}
                              />

                              {/* Status update indicator */}
                              {statusUpdateLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={16} />
                                  <Typography variant="caption" color="text.secondary">
                                    Updating status...
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Status help text */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block', fontSize: '0.7rem' }}
                          >
                            {resume.status === 'interview_scheduled' &&
                              'Interview panel has been assigned'}
                            {resume.status === 'reviewing' && 'Resume is under review'}
                            {resume.status === 'shortlisted' && 'Candidate has been shortlisted'}
                            {resume.status === 'interviewed' && 'Interview completed'}
                            {resume.status === 'hired' && 'Candidate has been hired'}
                            {resume.status === 'rejected' && 'Application rejected'}
                          </Typography>
                        </Box>
                       

                        {/* Notes */}
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem', mb: 1.5 }}
                          >
                            Notes & Comments
                          </Typography>
                          {editMode ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              value={formData.notes}
                              onChange={e => handleInputChange('notes', e.target.value)}
                              variant="outlined"
                              placeholder="Add notes or comments about this candidate..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: '#f9fafb',
                                  '&:hover': { backgroundColor: '#f3f4f6' },
                                  '&.Mui-focused': { backgroundColor: 'white' },
                                },
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: '#f9fafb',
                                borderRadius: 1.5,
                                border: '1px solid #e5e7eb',
                                minHeight: 70,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: resume.notes ? '#1f2937' : '#9ca3af',
                                  fontStyle: resume.notes ? 'normal' : 'italic',
                                  lineHeight: 1.5,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {resume.notes || 'No notes added yet...'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>

              {/* Right Column - Resume Preview */}
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e5e7eb',
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent
                    sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <WorkIcon sx={{ color: 'var(--primary-blue)', fontSize: 20 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}
                      >
                        Resume Preview
                      </Typography>
                    </Box>

                    {loadingPreview ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flex: 1,
                          gap: 2,
                        }}
                      >
                        <CircularProgress sx={{ color: 'var(--primary-blue)' }} size={32} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem' }}
                        >
                          Loading resume preview...
                        </Typography>
                      </Box>
                    ) : resumePreview && resumePreview.fileUrl ? (
                      <Box
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Box
                              sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: 'text.secondary' }}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                color="text.secondary"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                📄 {resumePreview.fileName || 'Resume file'}
                              </Typography>
                              {resumePreview.fileExtension && (
                                <Chip 
                                  label={resumePreview.fileExtension.toUpperCase()} 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem',
                                    backgroundColor: '#f0f9ff',
                                    color: 'var(--primary-blue)',
                                    fontWeight: 600
                                  }} 
                                />
                              )}
                            </Box>
                            {/* Debug info */}
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>
                              Preview Status: {resumePreview.fileUrl ? '✓ URL Ready' : '✗ No URL'} | 
                              Extension: {resumePreview.fileExtension || 'unknown'} | 
                              Type: {resumePreview.fileType || 'unknown'} | 
                              Size: {resumePreview.fileBlob ? `${(resumePreview.fileBlob.size / 1024).toFixed(2)} KB` : 'unknown'}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1.5,
                              borderRadius: 1,
                            }}
                          >
                            Download
                          </Button>
                        </Box>

                        {/* PDF Preview */}
                        {resumePreview.fileExtension === 'pdf' ? (
                          <Box
                            sx={{
                              flex: 1,
                              border: '1px solid #e5e7eb',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              backgroundColor: '#525659',
                              position: 'relative',
                            }}
                          >
                            <iframe
                              src={resumePreview.fileUrl}
                              title="Resume PDF Preview"
                              style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block',
                              } as React.CSSProperties}
                              allow="fullscreen"
                            />
                          </Box>
                        ) : resumePreview.fileExtension === 'doc' || resumePreview.fileExtension === 'docx' ? (
                          // Word Document - Preview not available for Word docs, show download option
                          <Box
                            sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 2,
                              p: 3,
                              border: '2px dashed #e5e7eb',
                              borderRadius: 1.5,
                              backgroundColor: '#f8fafc',
                            }}
                          >
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: '50%',
                                backgroundColor: '#f0f9ff',
                                border: '1px solid #bfdbfe',
                                mb: 1,
                              }}
                            >
                              <WorkIcon sx={{ fontSize: 40, color: 'var(--primary-blue)' }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 500, fontSize: '1rem' }}>
                              Word Document
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 320 }}>
                              Preview is not available for Word documents in the browser. Please download the file to view its contents.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={handleDownload}
                              sx={{
                                mt: 1,
                                backgroundColor: 'var(--primary-blue)',
                                textTransform: 'none',
                                fontWeight: 500,
                                borderRadius: 1.5,
                                px: 3,
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                '&:hover': {
                                  backgroundColor: 'var(--primary-blue)',
                                  filter: 'brightness(0.95)',
                                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                                },
                              }}
                            >
                              Download Document
                            </Button>
                          </Box>
                        ) : (
                          // Unsupported file type - show download message
                          <Box
                            sx={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 2,
                              p: 3,
                              border: '2px dashed #e5e7eb',
                              borderRadius: 1.5,
                              backgroundColor: '#f8fafc',
                            }}
                          >
                            <WorkIcon sx={{ fontSize: 48, color: '#d1d5db' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                              Preview not available for this file type ({resumePreview.fileExtension?.toUpperCase()})
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
                              Please download the file to view its contents
                            </Typography>
                          </Box>
                        )}

                        {/* Show extracted skills if available */}
                        {resumePreview.skills && resumePreview.skills.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                mb: 1.5,
                                color: '#1f2937',
                                fontSize: '0.875rem',
                              }}
                            >
                              🏷️ Extracted Skills
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {resumePreview.skills.map((skill: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#f0f9ff',
                                    color: 'var(--primary-blue)',
                                    border: '1px solid #bfdbfe',
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    '&:hover': {
                                      backgroundColor: '#e0f2fe',
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flex: 1,
                          gap: 2,
                          py: 3,
                        }}
                      >
                        {previewError ? (
                          // Show error message if there was an error
                          <>
                            <Alert severity="warning" sx={{ mb: 2, maxWidth: 400 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                <strong>Preview Error:</strong> {previewError}
                              </Typography>
                            </Alert>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: '50%',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                mb: 1,
                              }}
                            >
                              <WorkIcon sx={{ fontSize: 40, color: '#dc2626' }} />
                            </Box>
                          </>
                        ) : (
                          // No error, just no preview available
                          <Box
                            sx={{
                              p: 2.5,
                              borderRadius: '50%',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e5e7eb',
                              mb: 1,
                            }}
                          >
                            <WorkIcon sx={{ fontSize: 40, color: '#d1d5db' }} />
                          </Box>
                        )}
                        <Typography
                          variant="h6"
                          sx={{ color: '#9ca3af', fontWeight: 500, fontSize: '1.1rem' }}
                        >
                          {previewError ? 'Preview Failed' : 'Resume preview not available'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', mb: 2, fontSize: '0.875rem', maxWidth: 320 }}
                        >
                          {previewError 
                            ? 'The resume file could not be loaded. This may be due to file permissions or the file may not exist on the server.'
                            : 'The resume content could not be loaded for preview. You can download the file to view it directly.'
                          }
                        </Typography>
                        {/* Show debug info in development */}
                        {process.env.NODE_ENV === 'development' && resumePreview && (
                          <Alert severity="info" sx={{ mb: 2, maxWidth: 400, fontSize: '0.75rem' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              Debug Info:<br />
                              - Has fileUrl: {resumePreview.fileUrl ? 'Yes' : 'No'}<br />
                              - Has fileBlob: {resumePreview.fileBlob ? 'Yes' : 'No'}<br />
                              - File extension: {resumePreview.fileExtension || 'unknown'}<br />
                              - File type: {resumePreview.fileType || 'unknown'}<br />
                              - File name: {resumePreview.fileName || 'unknown'}
                            </Typography>
                          </Alert>
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownload}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'var(--primary-blue)',
                            color: 'var(--primary-blue)',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: '#f0f9ff',
                              borderColor: 'var(--primary-blue)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Download Resume
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Footer Actions with Status Update Loading */}
        {editMode && (
          <DialogActions
            sx={{
              px: 4,
              py: 3,
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              gap: 2,
            }}
          >
            <Button
              onClick={handleCancelEdit}
              startIcon={<CancelIcon />}
              disabled={statusUpdateLoading}
              sx={{
                color: '#6b7280',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 1.5,
                px: 3,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
              }}
            >
              Cancel Changes
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={
                loading || statusUpdateLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              disabled={loading || statusUpdateLoading}
              sx={{
                backgroundColor: 'var(--primary-blue)',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 1.5,
                px: 4,
                fontSize: '0.875rem',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  backgroundColor: 'var(--primary-blue)',
                  filter: 'brightness(0.95)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading || statusUpdateLoading ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ResumeViewUpdateDialog;
